/**
 * ModeToggle Component
 *
 * Shared toggle component for switching between Edit (Chat) and Audit modes.
 * Integrates with JotAudit introduction experiment to conditionally show diamond icon.
 */

import React, { useCallback, useMemo } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { useJotAuditIntroExperiment } from '@features/jotaudit';
import { Diamond as DiamondIcon } from '@mui/icons-material';
import { Badge, Box } from '@mui/material';

import type { SidebarMode } from './SidebarContainer';

export interface ModeToggleProps {
  /**
   * Current mode (chat or audit)
   */
  mode: SidebarMode;

  /**
   * Number of audit issues to display in badge
   */
  auditIssueCount: number;

  /**
   * Callback when mode changes
   */
  onChange: (event: React.MouseEvent<HTMLElement>, newMode: SidebarMode | null) => void;

  /**
   * Minimum width for tabs (optional, defaults to 90)
   */
  minWidth?: number;

  /**
   * Size of the segmented control
   */
  size?: 'small' | 'medium' | 'large';
}

/**
 * ModeToggle - Shared component for Edit/Audit mode switching
 *
 * Features:
 * - SegmentedControl for mode switching
 * - Badge showing audit issue count
 * - Conditional diamond icon (experiment-driven)
 * - Configurable minimum width
 */
export const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  auditIssueCount,
  onChange,
  minWidth = 90,
  size = 'small',
}) => {
  const { showDiamondIcon } = useJotAuditIntroExperiment();

  // Segmented control options for Edit/Audit toggle
  const segmentedOptions = useMemo(
    () => [
      {
        value: 'chat',
        label: 'Edit',
      },
      {
        value: 'audit',
        label: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>Audit</span>
            {showDiamondIcon && (
              <DiamondIcon
                sx={{
                  fontSize: 20,
                  color: 'primary.main',
                }}
              />
            )}
          </Box>
        ),
      },
    ],
    [showDiamondIcon],
  );

  // Adapter for SegmentedControl onChange
  const handleChange = useCallback(
    (newMode: string) => {
      onChange({} as React.MouseEvent<HTMLElement>, newMode as SidebarMode);
    },
    [onChange],
  );

  return (
    <Box
      sx={{
        '& .MuiTab-root': {
          minWidth,
        },
      }}
    >
      <Badge
        badgeContent={auditIssueCount}
        color="error"
        max={99}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.65rem',
            height: 16,
            minWidth: 16,
            right: 4,
            top: 4,
            lineHeight: 'normal',
          },
        }}
      >
        <SegmentedControl
          options={segmentedOptions}
          value={mode}
          onChange={handleChange}
          size={size}
          ariaLabel="Edit or audit mode"
        />
      </Badge>
    </Box>
  );
};
